/**
 * Timezone Utilities for TitleFlow
 * 
 * IMPORTANT: All timestamps in the system are stored in UTC.
 * All display and business logic should use America/New_York (EST/EDT).
 * 
 * This ensures:
 * - Users in India, Dubai, etc. see the same business time
 * - "Today" counters, year filters, SLA timers use US Eastern time
 * - DST is handled automatically via America/New_York timezone
 * 
 * Created: January 29, 2026
 */

// Business timezone for TitleFlow (handles DST automatically)
export const BUSINESS_TIMEZONE = 'America/New_York';

/**
 * Get current time in the business timezone (America/New_York)
 * Use this instead of new Date() for any business logic
 * @returns {Date} Current date/time adjusted to EST/EDT
 */
export const getBusinessNow = () => {
  // Create a date formatter for America/New_York
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: BUSINESS_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  
  const parts = formatter.formatToParts(new Date());
  const values = {};
  parts.forEach(({ type, value }) => {
    values[type] = value;
  });
  
  // Return a Date object representing "now" in EST/EDT
  return new Date(
    parseInt(values.year),
    parseInt(values.month) - 1,
    parseInt(values.day),
    parseInt(values.hour),
    parseInt(values.minute),
    parseInt(values.second)
  );
};

/**
 * Get current year in business timezone
 * Use for year filters
 * @returns {number} Current year in EST/EDT
 */
export const getBusinessYear = () => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: BUSINESS_TIMEZONE,
    year: 'numeric'
  });
  return parseInt(formatter.format(new Date()));
};

/**
 * Get current month in business timezone (1-12)
 * @returns {number} Current month in EST/EDT
 */
export const getBusinessMonth = () => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: BUSINESS_TIMEZONE,
    month: 'numeric'
  });
  return parseInt(formatter.format(new Date()));
};

/**
 * Get current day in business timezone (1-31)
 * @returns {number} Current day in EST/EDT
 */
export const getBusinessDay = () => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: BUSINESS_TIMEZONE,
    day: 'numeric'
  });
  return parseInt(formatter.format(new Date()));
};

/**
 * Get today's date string in business timezone (YYYY-MM-DD)
 * Use for "today" filters
 * @returns {string} Today's date in EST/EDT as YYYY-MM-DD
 */
export const getBusinessToday = () => {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: BUSINESS_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  return formatter.format(new Date()); // Returns YYYY-MM-DD format
};

/**
 * Convert UTC date/timestamp to business timezone display string
 * @param {string|Date} utcDate - UTC date string or Date object
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string in EST/EDT
 */
export const formatToBusinessTime = (utcDate, options = {}) => {
  if (!utcDate) return '';
  
  const date = utcDate instanceof Date ? utcDate : new Date(utcDate);
  
  if (isNaN(date.getTime())) return '';
  
  const defaultOptions = {
    timeZone: BUSINESS_TIMEZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    ...options
  };
  
  return new Intl.DateTimeFormat('en-US', defaultOptions).format(date);
};

/**
 * Format date for display (date only, no time)
 * @param {string|Date} utcDate - UTC date string or Date object
 * @returns {string} Formatted date string (e.g., "Jan 29, 2026")
 */
export const formatBusinessDate = (utcDate) => {
  return formatToBusinessTime(utcDate, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: undefined,
    minute: undefined,
    hour12: undefined
  });
};

/**
 * Format date and time for display
 * @param {string|Date} utcDate - UTC date string or Date object
 * @returns {string} Formatted datetime string (e.g., "Jan 29, 2026, 3:45 PM")
 */
export const formatBusinessDateTime = (utcDate) => {
  return formatToBusinessTime(utcDate, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Format time only for display
 * @param {string|Date} utcDate - UTC date string or Date object
 * @returns {string} Formatted time string (e.g., "3:45 PM")
 */
export const formatBusinessTime = (utcDate) => {
  return formatToBusinessTime(utcDate, {
    year: undefined,
    month: undefined,
    day: undefined,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Format date in MM/DD/YYYY format
 * @param {string|Date} utcDate - UTC date string or Date object
 * @returns {string} Formatted date string (e.g., "01/29/2026")
 */
export const formatBusinessDateShort = (utcDate) => {
  if (!utcDate) return '';
  
  const date = utcDate instanceof Date ? utcDate : new Date(utcDate);
  
  if (isNaN(date.getTime())) return '';
  
  return new Intl.DateTimeFormat('en-US', {
    timeZone: BUSINESS_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date);
};

/**
 * Check if a UTC date is "today" in business timezone
 * @param {string|Date} utcDate - UTC date string or Date object
 * @returns {boolean} True if the date is today in EST/EDT
 */
export const isBusinessToday = (utcDate) => {
  if (!utcDate) return false;
  
  const date = utcDate instanceof Date ? utcDate : new Date(utcDate);
  if (isNaN(date.getTime())) return false;
  
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: BUSINESS_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  
  const dateStr = formatter.format(date);
  const todayStr = getBusinessToday();
  
  return dateStr === todayStr;
};

/**
 * Get start of day in business timezone as UTC Date
 * Use for date range queries
 * @param {Date} date - Date to get start of day for (defaults to today)
 * @returns {Date} Start of day in EST/EDT as UTC Date object
 */
export const getBusinessDayStart = (date = new Date()) => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: BUSINESS_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  
  const parts = formatter.formatToParts(date);
  const values = {};
  parts.forEach(({ type, value }) => {
    values[type] = value;
  });
  
  // Create a date string for midnight in EST/EDT
  const estMidnight = `${values.year}-${values.month}-${values.day}T00:00:00`;
  
  // Parse as EST/EDT and return as Date (will be UTC internally)
  return new Date(new Date(estMidnight).toLocaleString('en-US', { timeZone: BUSINESS_TIMEZONE }));
};

/**
 * Get the year from a UTC date in business timezone
 * @param {string|Date} utcDate - UTC date string or Date object
 * @returns {number} Year in EST/EDT
 */
export const getYearInBusinessTimezone = (utcDate) => {
  if (!utcDate) return getBusinessYear();
  
  const date = utcDate instanceof Date ? utcDate : new Date(utcDate);
  if (isNaN(date.getTime())) return getBusinessYear();
  
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: BUSINESS_TIMEZONE,
    year: 'numeric'
  });
  
  return parseInt(formatter.format(date));
};

/**
 * Format relative time (e.g., "2 hours ago", "just now")
 * @param {string|Date} utcDate - UTC date string or Date object
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (utcDate) => {
  if (!utcDate) return '';
  
  const date = utcDate instanceof Date ? utcDate : new Date(utcDate);
  if (isNaN(date.getTime())) return '';
  
  const now = new Date();
  const diffMs = now - date;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  
  return formatBusinessDate(date);
};

/**
 * Get timezone label for display (e.g., "EST" or "EDT")
 * @returns {string} Current timezone abbreviation
 */
export const getBusinessTimezoneLabel = () => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: BUSINESS_TIMEZONE,
    timeZoneName: 'short'
  });
  
  const parts = formatter.formatToParts(new Date());
  const tzPart = parts.find(p => p.type === 'timeZoneName');
  return tzPart ? tzPart.value : 'ET';
};

// Export default object for convenience
export default {
  BUSINESS_TIMEZONE,
  getBusinessNow,
  getBusinessYear,
  getBusinessMonth,
  getBusinessDay,
  getBusinessToday,
  formatToBusinessTime,
  formatBusinessDate,
  formatBusinessDateTime,
  formatBusinessTime,
  formatBusinessDateShort,
  isBusinessToday,
  getBusinessDayStart,
  getYearInBusinessTimezone,
  formatRelativeTime,
  getBusinessTimezoneLabel
};
